import {
  MdChecklist,
  MdStar,
  MdOutlineNoteAlt,
  MdSupportAgent,
  MdBackup,
} from "react-icons/md";
import { FiClipboard } from "react-icons/fi";
import { LuFileText } from "react-icons/lu";
import {
  FaUsers,
  FaBuilding,
  FaCalendarAlt,
  FaUsersCog,
  FaFileInvoiceDollar,
  FaTools,
  FaMobileAlt,
} from "react-icons/fa";

// Single source of truth for the pricing feature-comparison table.
// Both the desktop grid (all 3 plan columns) and the mobile dropdown
// (one plan at a time) render from this list, so they can never drift apart.
export const featureCategories = [
  {
    title: "Essential",
    icon: MdChecklist,
    rows: [
      {
        label: "Projects & Tasks",
        tooltip: "Organize work using projects, tasks, and structured task flows.",
        starter: { type: "text", text: "Unlimited" },
        pro: { type: "text", text: "Unlimited" },
        elite: { type: "text", text: "Unlimited" },
      },
      {
        label: "Clients",
        tooltip: "Manage and organize unlimited client records in your workspace.",
        starter: { type: "text", text: "Unlimited" },
        pro: { type: "text", text: "Unlimited" },
        elite: { type: "text", text: "Unlimited" },
      },
      {
        label: "Page History",
        tooltip: "Access previous versions and restore changes made over time.",
        starter: { type: "text", text: "30 days" },
        pro: { type: "text", text: "90 days" },
        elite: { type: "text", text: "Unlimited" },
      },
    ],
  },
  {
    title: "Core Features",
    icon: MdStar,
    rows: [
      {
        label: "Dashboards",
        tooltip: "Interactive dashboards to monitor and manage your projects, tasks, and team metrics.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Multiple Dashboards",
        tooltip: "Create and switch between multiple custom dashboards for different teams or workflows.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Inbox",
        tooltip: "Centralized inbox to view all updates, notifications, and task assignments.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Favorites",
        tooltip: "Quickly access your most important projects, tasks, or files by marking them as favorites.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Trash",
        tooltip: "Recover deleted items or permanently remove them from your workspace.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Task Master Settings",
        tooltip: "Advanced configuration options to customize task behavior and workflow rules.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Status Templates",
        tooltip: "Pre-defined or custom status workflows to track task progression efficiently.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Layout Templates",
        tooltip: "Ready-to-use board, list, and table layouts for different types of projects.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "User Controls",
        tooltip: "Manage user permissions, roles, and access rights across the workspace.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Analytical Settings",
        tooltip: "Configure parameters for tracking performance, time logs, and custom metrics.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Support Tickets",
        tooltip: "Submit and track support requests directly from your workspace dashboard.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Projects & Tasks",
    icon: FiClipboard,
    rows: [
      {
        label: "Task Workflow System",
        tooltip: "Structured task flows to manage work from start to completion.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Multiple Tasks Views",
        tooltip: "View work in different layouts for easier planning and tracking.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Multiple Assignees",
        tooltip: "Assign one task to multiple team members where needed.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Sub-tasks",
        tooltip: "Break larger tasks into smaller actionable parts.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Different View (Table / Calendar / Capsule View)",
        tooltip: "Switch between Table, Calendar, and Capsule views for flexible project visualization.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Checklists",
    icon: MdChecklist,
    rows: [
      {
        label: "Checklists",
        tooltip: "Add simple checklists inside tasks to track step-by-step progress.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Advance Checklists",
        tooltip: "Smarter to-dos with assignees, due dates, and structured execution flows.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Private Notes",
    icon: MdOutlineNoteAlt,
    rows: [
      {
        label: "Powerful Note-taking",
        tooltip: "Create rich notes for personal work, documentation, and daily execution.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Notes Collaboration",
        tooltip: "Work on notes together with your team in shared mode.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Teams",
    icon: FaUsers,
    rows: [
      {
        label: "Team Lead Setup",
        tooltip: "Assign team leads for supervision, review, and workflow control.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Team Lead Dashboard",
        tooltip: "Give team leads a focused dashboard to manage team work and approvals.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Office Hub",
    icon: FaBuilding,
    rows: [
      {
        label: "User Requests",
        tooltip: "Manage internal requests raised by team members in one place.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Attendance",
        tooltip: "Track attendance and related team records inside the workspace.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Policies",
        tooltip: "Store and access office policies for team reference.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Timesheets",
        tooltip: "Record and manage time entries for work done by users.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Documents",
    icon: LuFileText,
    rows: [
      {
        label: "Documents Management",
        tooltip: "Upload, organize, and manage files across your workspace.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "User Centric Documents",
        tooltip: "Keep documents mapped user-wise for easier access and tracking.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Client Wise Documents",
        tooltip: "Organize files client-wise for faster retrieval and cleaner records.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Client Access Toggle",
        tooltip: "Control whether clients can access specific documents or areas.",
        starter: { type: "cross" },
        pro: { type: "cross" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Calendars & Reminders",
    icon: FaCalendarAlt,
    rows: [
      {
        label: "Calendar Dashboard",
        tooltip: "View deadlines, events, and schedules in one calendar-based dashboard.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Reminders Dashboard",
        tooltip: "Never forget again. Reminders that link back to work.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Expiring Task Reminders",
        tooltip: "Never forget again. Reminders that link back to work.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "User & Client Management",
    icon: FaUsersCog,
    rows: [
      {
        label: "Easy User & Client Dashboard",
        tooltip: "Less admin work. More insight.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "User Groups and Team Leader Setup",
        tooltip: "Create groups and assign leaders in seconds.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Log Reports",
        tooltip: "Get full visibility of team and client login sessions.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Value Analysis",
        tooltip: "Measure growth, efficiency, and business impact with smart analytics.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Work Utilities",
        tooltip: "Manage daily operations smoothly using powerful work tools.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Clients Login",
        tooltip: "Allow clients to log in and access their dedicated workspace area.",
        starter: { type: "cross" },
        pro: { type: "cross" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Bill Flow",
    icon: FaFileInvoiceDollar,
    rows: [
      {
        label: "Client Wise Bill Flow / Task Invoicing",
        tooltip: "Turn completed tasks into invoices, organized by client.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Clients Group Wise Bill Flow / Task Invoicing",
        tooltip: "Generate grouped invoices across multiple clients for streamlined billing.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Utilities",
    icon: FaTools,
    rows: [
      {
        label: "Bulk Task Uploads",
        tooltip: "Got Excel? You’ve got Simplifyte.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Bulk Client Uploads",
        tooltip: "Bring in all your clients in one shot.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Bulk Calendar Events Uploads",
        tooltip: "Import deadlines, events, and more from your sheet.",
        starter: { type: "cross" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Mobile App Access (Coming soon)",
    icon: FaMobileAlt,
    rows: [
      {
        label: "Access to full app (Users and Clients)",
        tooltip: "Work on the go, without compromise.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Data Backup",
    icon: MdBackup,
    rows: [
      {
        label: "Restoring of data in case of data loss",
        tooltip: "Restore your work if anything goes wrong.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
    ],
  },
  {
    title: "Support",
    icon: MdSupportAgent,
    rows: [
      {
        label: "Help center",
        tooltip: "Your support library, always open.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Email support",
        tooltip: "We respond, we don't ghost.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "WhatsApp Support 24x7",
        tooltip: "Get instant help anytime on WhatsApp.",
        starter: { type: "check" },
        pro: { type: "check" },
        elite: { type: "check" },
      },
      {
        label: "Priority support",
        tooltip: "Skip the queue — talk to experts.",
        starter: { type: "cross" },
        pro: { type: "cross" },
        elite: { type: "check" },
      },
      {
        label: "Migration assistance",
        tooltip: "Switch without the stress.",
        starter: { type: "text", text: "Talk to Sales" },
        pro: { type: "text", text: "Talk to Sales" },
        elite: { type: "text", text: "Talk to Sales" },
      },
    ],
  },
];
